import { useTranslation } from 'react-i18next'
import { ClipboardList, PlayCircle, CheckCircle2, AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buildNodeIconPath } from '@/lib/imagePaths'
import { EntityIcon } from '@/components/ui/entity-icon'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import type { RecoveryObject } from '../../api'
import type { Change } from './location-updates'

// Same clock-with-tick glyph used for the forecast/actual finish on the recovery canvas.
function ClockTickIcon({ className }: { className?: string }) {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
            <circle cx="10" cy="12" r="8" />
            <polyline points="10 7 10 12 13 13.5" />
            <path d="m15 18 2.5 2.5 4.5-5" />
        </svg>
    )
}

/** Old value struck through, new value after it: what a person needs without remembering the old one. */
function Was({ from, to }: { from: string; to: string }) {
    return (
        <>
            <s className="text-muted-foreground">{from}</s> <span className="font-medium">{to}</span>
        </>
    )
}

/**
 * A single recovery object: icon, id, description, engineer, runbook progress.
 *
 * With an unseen `update` it is marked (and sits at the top of its column), says what changed
 * since it was last seen, and clicking it marks it as seen. A new issue marks it red, so a problem
 * does not read as one more piece of progress.
 */
export function RecoveryObjectCard({
    obj, update = null, flashing = false, onSeen,
}: {
    obj: RecoveryObject
    update?: { at: number; changes: Change[] } | null
    flashing?: boolean
    onSeen?: () => void
}) {
    const { t, i18n } = useTranslation()
    const nf = new Intl.NumberFormat(i18n.language || undefined)
    const hasIssue = !!update?.changes.some((c) => c.kind === 'issue')
    const updatedAt = update
        ? new Date(update.at).toLocaleTimeString(i18n.language || undefined, { hour: '2-digit', minute: '2-digit', hour12: false })
        : null
    const none = t('recoverRequests.locationTracking.none', 'None')
    const changeLine = (c: Change, i: number) => {
        switch (c.kind) {
            case 'new':
                return <div key={i}>{t('recoverRequests.locationTracking.changeNew', 'New at this location')}</div>
            case 'issue':
                return <div key={i} className="font-medium text-destructive">{t('recoverRequests.locationTracking.changeIssue', '{{count}} new issue', { count: c.count })}</div>
            case 'status':
                return <div key={i}>{t('recoverRequests.locationTracking.changeStatus', 'Status')}: <Was from={c.from || none} to={c.to || none} /></div>
            case 'engineer':
                return <div key={i}>{t('recoverRequests.locationTracking.changeEngineer', 'Engineer')}: <Was from={c.from || t('recoverRequests.locationTracking.unassigned', 'Unassigned')} to={c.to || t('recoverRequests.locationTracking.unassigned', 'Unassigned')} /></div>
            case 'tasks': {
                const diff = c.to - c.from
                return (
                    <div key={i}>
                        {t('recoverRequests.locationTracking.changeTasks', 'Tasks')}: <Was from={nf.format(c.from)} to={nf.format(c.to)} />
                        {c.total > 0 && <span className="text-muted-foreground"> / {nf.format(c.total)}</span>}
                        {diff !== 0 && <span className={diff > 0 ? 'ml-1 text-success' : 'ml-1 text-muted-foreground'}>({diff > 0 ? '+' : ''}{nf.format(diff)})</span>}
                    </div>
                )
            }
        }
    }

    const total = obj.tasksTotal || 0
    const completed = obj.tasksCompleted || 0
    const started = obj.tasksStarted || 0
    const issues = obj.tasksIssue || 0
    const pct = total > 0
        ? Math.min(100, Math.round(obj.percentComplete != null ? obj.percentComplete : (completed / total) * 100))
        : 0
    const statusLc = (obj.status || '').toLowerCase()
    const isIssue = statusLc.includes('issue') || statusLc.includes('block') || statusLc.includes('fail')

    const finishIso = obj.actualFinishTime || obj.forecastFinishTime
    const finishLabel = (() => {
        if (!finishIso) return null
        try {
            return new Date(finishIso).toLocaleString(i18n.language || undefined, {
                month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
            })
        } catch { return null }
    })()

    // The runbook task breakdown (total / in-progress / completed / issues). It used to sit on
    // every card and made the board too busy — now it's surfaced on hover (see tooltip below).
    const taskBreakdown = (
        <div className="flex items-center gap-1.5">
            <span className="flex items-center gap-1 rounded px-1.5 py-0.5 bg-primary/10" title={t('recoverRequests.tasksTotal', 'Tasks')}>
                <ClipboardList className="h-3.5 w-3.5 text-primary" />
                <span className="text-xs font-medium tabular-nums">{nf.format(total)}</span>
            </span>
            <span className="flex items-center gap-1 rounded px-1.5 py-0.5 bg-amber-500/10" title={t('recoverRequests.tasksInProgress', 'In progress')}>
                <PlayCircle className="h-3.5 w-3.5 text-amber-600" />
                <span className="text-xs font-medium tabular-nums">{nf.format(started)}</span>
            </span>
            <span className="flex items-center gap-1 rounded px-1.5 py-0.5 bg-green-500/10" title={t('recoverRequests.tasksCompleted', 'Completed')}>
                <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                <span className="text-xs font-medium tabular-nums">{nf.format(completed)}</span>
            </span>
            <span className="flex items-center gap-1 rounded px-1.5 py-0.5 bg-red-500/10" title={t('recoverRequests.tasksIssues', 'Issues')}>
                <AlertCircle className="h-3.5 w-3.5 text-red-600" />
                <span className="text-xs font-medium tabular-nums">{nf.format(issues)}</span>
            </span>
        </div>
    )

    const card = (
        <div
            className={cn(
                'rounded-lg border bg-card p-3 transition-colors',
                update && !hasIssue && 'cursor-pointer border-primary bg-primary/5 ring-1 ring-primary/40',
                update && hasIssue && 'cursor-pointer border-destructive bg-destructive/5 ring-1 ring-destructive/40',
                flashing && 'animate-pulse ring-2',
            )}
            data-updated={update ? 'true' : undefined}
            onClick={update ? onSeen : undefined}
            title={update ? t('recoverRequests.locationTracking.clickToMarkSeen', 'Click to mark as seen') : undefined}
        >
            {update && (
                <div className="mb-2 flex items-center justify-between gap-2 text-[11px]">
                    <span className={cn('rounded-full px-2 py-0.5 font-medium', hasIssue ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary')}>
                        {t('recoverRequests.locationTracking.updatedAt', 'Updated {{time}}', { time: updatedAt })}
                    </span>
                </div>
            )}
            <div className="flex items-center gap-2">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted/30">
                    <EntityIcon src={buildNodeIconPath(obj.icon)} alt="" className="h-7 w-7" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-foreground">{obj.id}</div>
                    <div className="truncate text-xs text-muted-foreground" title={obj.description}>
                        {obj.description}
                    </div>
                </div>
            </div>

            {update && update.changes.length > 0 && (
                <div className="mt-2 space-y-0.5 text-xs" data-testid="card-changes">
                    {update.changes.map(changeLine)}
                </div>
            )}

            <div className="mt-2 flex items-center justify-between gap-2 text-xs">
                <span className="min-w-0 truncate">
                    {obj.engineer ? (
                        <span className="text-foreground">
                            <span className="text-muted-foreground">{t('recoverRequests.locationTracking.engineer', 'Engineer:')}</span> {obj.engineer}
                        </span>
                    ) : (
                        <span className="italic text-muted-foreground">{t('recoverRequests.locationTracking.unassigned', 'Unassigned')}</span>
                    )}
                </span>
                {finishLabel && (
                    <span
                        className="flex shrink-0 items-center gap-1 text-muted-foreground"
                        title={t(obj.actualFinishTime ? 'recoverRequests.locationTracking.actualFinish' : 'recoverRequests.locationTracking.forecastFinish', obj.actualFinishTime ? 'Actual finish' : 'Forecast finish')}
                    >
                        <ClockTickIcon className="h-3.5 w-3.5 text-[#008000]" />
                        <span className="tabular-nums">{finishLabel}</span>
                    </span>
                )}
            </div>

            {total > 0 ? (
                <div className="mt-2 flex items-center gap-2">
                    <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">{nf.format(completed)}/{nf.format(total)}</span>
                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                        <div
                            className={cn('h-full rounded-full transition-all', isIssue ? 'bg-destructive' : 'bg-success')}
                            style={{ width: `${pct}%` }}
                        />
                    </div>
                    <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">{pct}%</span>
                </div>
            ) : (
                <div className="mt-2 text-xs italic text-muted-foreground">{t('recoverRequests.noRunbook', 'No runbook available')}</div>
            )}
        </div>
    )

    // No runbook → nothing to reveal on hover.
    if (total === 0) return card

    return (
        <TooltipProvider delayDuration={150}>
            <Tooltip>
                <TooltipTrigger asChild>{card}</TooltipTrigger>
                <TooltipContent side="top" className="p-2">
                    <div className="mb-1 text-[11px] font-medium text-muted-foreground">{t('recoverRequests.runbookTasks', 'Runbook tasks')}</div>
                    {taskBreakdown}
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    )
}
